import React, { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { useCreateRoleOptions } from '../CreateRoleDrawer/useCreateRoleOptions';
import { BreadcrumbsWrap } from '../IdentityDetails/styles';
import { getDefaultExpandedPermissionIds } from './mapRolePermissionTree';
import { toRoleDetailsFormValues } from './formUtils';
import {
  mapApiSelectionToCatalogSelection,
  mapCreateRoleContentsToPermissionNodes,
} from './mapEditRolePermissionCatalog';
import { mapApiRoleToDetails } from './mapApiRole';
import { mapRoleDetailsToUpdateRequest } from './mapRoleDetailsUpdateRequest';
import { BackToRoles } from './BackToRoles';
import { RoleDetailsGeneralSection } from './RoleDetailsGeneralSection';
import { RoleDetailsModifiersSection } from './RoleDetailsModifiersSection';
import { RolePermissionTree } from './RolePermissionTree';
import {
  DetailsInfoColumn,
  DetailsLayout,
  DetailsRolesColumn,
  PermissionTreeError,
  RoleCardActions,
  RoleCardHeader,
  RoleCardTitle,
  RoleDetailsCard,
  RoleTreePanel,
  SectionDivider,
} from './styles';
import { filterPermissionTreeToSelection } from './treeUtils';
import { useUpdateTeamRole } from './useUpdateTeamRole';
import { roleDetailsValidationSchema } from './validationSchema';
import {
  ROLE_CATEGORIES,
  RoleDetailsFormValues,
  RoleDetailsModel,
  RolePermissionNode,
  RoleSelectOption,
} from './types';

interface RoleDetailsProps {
  roleDetails: RoleDetailsModel;
}

function buildTeamOptions(
  currentTeamId: string,
  currentTeamName: string,
  teamOptionsFromApi: RoleSelectOption[],
): RoleSelectOption[] {
  if (teamOptionsFromApi.some(option => option.value === currentTeamId)) {
    return teamOptionsFromApi;
  }

  if (!currentTeamId) {
    return teamOptionsFromApi;
  }

  return [
    { label: currentTeamName, value: currentTeamId },
    ...teamOptionsFromApi,
  ];
}

function resolvePermissionTreeItems({
  isEditing,
  canEdit,
  editPermissionCatalog,
  savedRoleDetails,
  selectedPermissionIds,
}: {
  isEditing: boolean;
  canEdit: boolean;
  editPermissionCatalog: RolePermissionNode[];
  savedRoleDetails: RoleDetailsModel;
  selectedPermissionIds: string[];
}): RolePermissionNode[] {
  if (isEditing && canEdit) {
    return editPermissionCatalog;
  }

  if (savedRoleDetails.usesInclusionTree) {
    return savedRoleDetails.permissionTree;
  }

  return filterPermissionTreeToSelection(
    savedRoleDetails.permissionTree,
    selectedPermissionIds,
  );
}

export function RoleDetails({ roleDetails }: RoleDetailsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const canEdit = roleDetails.category === ROLE_CATEGORIES.custom;
  const createRoleOptions = useCreateRoleOptions(canEdit);
  const updateTeamRole = useUpdateTeamRole();
  const [savedRoleDetails, setSavedRoleDetails] =
    useState<RoleDetailsModel>(roleDetails);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedPermissionIds, setExpandedPermissionIds] = useState<string[]>(
    () => getDefaultExpandedPermissionIds(roleDetails.permissionTree),
  );
  const editPermissionCatalog = useMemo(
    () =>
      mapCreateRoleContentsToPermissionNodes(createRoleOptions.contentsTree),
    [createRoleOptions.contentsTree],
  );
  const teamOptions = useMemo(
    () =>
      buildTeamOptions(
        savedRoleDetails.teamId,
        savedRoleDetails.teamName,
        createRoleOptions.teamOptions,
      ),
    [
      createRoleOptions.teamOptions,
      savedRoleDetails.teamId,
      savedRoleDetails.teamName,
    ],
  );

  const { control, handleSubmit, reset } = useForm<RoleDetailsFormValues>({
    resolver: yupResolver(roleDetailsValidationSchema),
    defaultValues: toRoleDetailsFormValues(roleDetails),
    mode: 'onChange',
  });

  function handleStartEditing() {
    const catalogSelection = mapApiSelectionToCatalogSelection(
      createRoleOptions.functionalRoles,
      savedRoleDetails.selectedPermissionIds,
    );
    reset({
      ...toRoleDetailsFormValues(savedRoleDetails),
      selectedPermissionIds: catalogSelection,
    });
    setExpandedPermissionIds(
      getDefaultExpandedPermissionIds(editPermissionCatalog),
    );
    setIsEditing(true);
  }

  function handleCancelEditing() {
    reset(toRoleDetailsFormValues(savedRoleDetails));
    setExpandedPermissionIds(
      getDefaultExpandedPermissionIds(savedRoleDetails.permissionTree),
    );
    setIsEditing(false);
  }

  async function handleSave(values: RoleDetailsFormValues) {
    try {
      const request = mapRoleDetailsToUpdateRequest(
        values,
        createRoleOptions.functionalRoles,
      );
      const updatedRole = await updateTeamRole.mutateAsync({
        teamId: values.teamId,
        roleId: savedRoleDetails.id,
        request,
      });
      const nextRoleDetails = mapApiRoleToDetails(updatedRole);
      setSavedRoleDetails(nextRoleDetails);
      reset(toRoleDetailsFormValues(nextRoleDetails));
      setExpandedPermissionIds(
        getDefaultExpandedPermissionIds(nextRoleDetails.permissionTree),
      );
      setIsEditing(false);
      enqueueSnackbar('Role updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to update role'), {
        variant: 'error',
      });
    }
  }

  useEffect(() => {
    setSavedRoleDetails(roleDetails);
    reset(toRoleDetailsFormValues(roleDetails));
    setExpandedPermissionIds(
      getDefaultExpandedPermissionIds(roleDetails.permissionTree),
    );
    setIsEditing(false);
  }, [reset, roleDetails]);

  const isSaving = updateTeamRole.isPending;

  return (
    <form noValidate onSubmit={handleSubmit(handleSave)}>
      <BackToRoles roleCategory={savedRoleDetails.category} />
      <BreadcrumbsWrap>
        <Breadcrumbs
          separator={<NavigateNextIcon color="action" fontSize="small" />}
        >
          <Typography color="textSecondary" variant="body1">
            Role Management
          </Typography>
          <Typography color="textPrimary" variant="body1">
            {savedRoleDetails.name}
          </Typography>
        </Breadcrumbs>
      </BreadcrumbsWrap>
      <DetailsLayout>
        <DetailsInfoColumn>
          <RoleDetailsGeneralSection
            isEditing={isEditing}
            control={control}
            name={savedRoleDetails.name}
            teamName={savedRoleDetails.teamName}
            description={savedRoleDetails.description}
            teamOptions={teamOptions}
          />
          <SectionDivider />
          <RoleDetailsModifiersSection
            isEditing={isEditing}
            control={control}
            modifiers={savedRoleDetails.modifiers}
          />
        </DetailsInfoColumn>
        <DetailsRolesColumn>
          <RoleDetailsCard>
            <RoleCardHeader>
              <RoleCardTitle color="textPrimary" variant="h6">
                {savedRoleDetails.usesInclusionTree
                  ? 'Included roles & attributes'
                  : 'Attributes'}
              </RoleCardTitle>
              {canEdit && !isEditing && (
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleStartEditing}
                  disabled={
                    createRoleOptions.isLoadingContents ||
                    createRoleOptions.isErrorContents
                  }
                >
                  Edit
                </Button>
              )}
              {isEditing && (
                <RoleCardActions>
                  <Button
                    variant="outlined"
                    color="primary"
                    type="button"
                    onClick={handleCancelEditing}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving…' : 'Save changes'}
                  </Button>
                </RoleCardActions>
              )}
            </RoleCardHeader>
            <RoleTreePanel>
              {isEditing && createRoleOptions.isLoadingContents ? (
                <CircularProgress size={32} />
              ) : null}
              {isEditing && createRoleOptions.isErrorContents ? (
                <Typography color="error">
                  Failed to load role contents. Try again later.
                </Typography>
              ) : null}
              {!isEditing && savedRoleDetails.permissionTree.length === 0 ? (
                <Typography color="textSecondary">
                  {savedRoleDetails.usesInclusionTree
                    ? 'No included roles or attributes are configured for this role.'
                    : 'No attributes are configured for this role.'}
                </Typography>
              ) : null}
              {!(isEditing && createRoleOptions.isLoadingContents) ? (
                <Controller
                  name="selectedPermissionIds"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <RolePermissionTree
                        items={resolvePermissionTreeItems({
                          isEditing,
                          canEdit,
                          editPermissionCatalog,
                          savedRoleDetails,
                          selectedPermissionIds: field.value,
                        })}
                        selectedPermissionIds={field.value}
                        expandedPermissionIds={expandedPermissionIds}
                        disabled={!isEditing}
                        onSelectedPermissionIdsChange={field.onChange}
                        onExpandedPermissionIdsChange={setExpandedPermissionIds}
                      />
                      {error && (
                        <PermissionTreeError>
                          {error.message}
                        </PermissionTreeError>
                      )}
                    </>
                  )}
                />
              ) : null}
            </RoleTreePanel>
          </RoleDetailsCard>
        </DetailsRolesColumn>
      </DetailsLayout>
    </form>
  );
}
